/**
 * The rest of a studio draft: the sources the person brought, notes, what
 * RecordOS cannot hold yet, where each part came from, and the gaps the person
 * chose to leave.
 *
 * A source id is the uuid of an `app.agent_file` row; whether that row exists
 * and is the person's is the store's check, never the shared judge's.
 */
import { UNMET_NEED_LEVELS } from '../compose';
import { STUDIO_LIMITS, type StudioDraft, type StudioNotYet } from './draft';
import type { StudioProblem } from './judge';
import { studioPartExists } from './paths';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GAP_ID_MAX = 80;

/** The wishes for a link that reads an entry's lines: stock down per line of a bill, or any link over lines. */
const LINE_WISHES: ReadonlySet<string> = new Set(['not_yet.stock_from_lines', 'link.reads_lines']);
const STOCK_WORDS = /\b(stock|inventory|maal|godown)\b/iu;

/** Whether a wish is for a link that reads an entry's lines: its capability says so, or it is a link of stock. */
export const wishReadsLines = (item: Pick<StudioNotYet, 'kind' | 'name' | 'need' | 'capability'>): boolean =>
  (item.capability !== undefined && LINE_WISHES.has(item.capability)) || (item.kind === 'link' && STOCK_WORDS.test(`${item.need} ${item.name}`));

/** Whether any record of the draft holds lines: several items on one entry. */
export const draftHoldsLines = (draft: Pick<StudioDraft, 'records'>): boolean => draft.records.some((record) => record.fields.some((field) => field.type === 'lines'));

/**
 * Whether a wish the draft keeps may be said as not possible yet, on the
 * canvas or the Build sheet (R4 of docs/plans/qa-fixes-2.md). Stock is never
 * "not possible": the only honest not-yet is a link that reads a bill's lines
 * (Q1), so a wish for one stands only where a record holds lines. Run 2 drew
 * "Not a link RecordOS can make yet" for a sale of one item an entry, which a
 * link builds. Every other wish stands.
 *
 * The turn's own line link stands too, though no record holds lines yet: it
 * is filed when the person said a bill holds several items beside the sale's
 * one-item stock link, and the turn draws no lines field for it (S5 of run
 * 2). It is told by the name the server gives it, "<record> item lines" or
 * "<record> item lines into <record>" (`links.lines_ghost_name`), of a record
 * the draft holds.
 */
export const notYetStands = (draft: Pick<StudioDraft, 'records'>, item: Pick<StudioNotYet, 'kind' | 'name' | 'need' | 'capability'>): boolean =>
  !wishReadsLines(item) || draftHoldsLines(draft) || isTurnLineLink(draft, item);

const fold = (words: string): string => words.toLowerCase().replace(/\s+/g, ' ').trim();

/** Whether a wish is the line link the turn files: a link named "<record> item lines[ into <record>]" of the draft's records. */
const isTurnLineLink = (draft: Pick<StudioDraft, 'records'>, item: Pick<StudioNotYet, 'kind' | 'name'>): boolean => {
  if (item.kind !== 'link') return false;
  const name = fold(item.name);
  const names = new Set(draft.records.map((record) => fold(record.name)));
  const at = name.indexOf(' item lines');
  if (at <= 0 || !names.has(name.slice(0, at))) return false;
  const rest = name.slice(at + ' item lines'.length);
  return rest === '' || (rest.startsWith(' into ') && names.has(rest.slice(' into '.length)));
};

const within = (text: string, max: number): boolean => text.trim().length > 0 && text.trim().length <= max;

export const notesProblems = (draft: StudioDraft): StudioProblem[] => {
  const problems: StudioProblem[] = [];
  const say = (code: string, message: string, path: string): void => {
    problems.push({ code, message, path, part: path });
  };
  const listed = new Set(draft.sources.map((source) => source.id));
  const sourceNamed = (id: string, path: string): void => {
    if (!listed.has(id)) say('SOURCE_NOT_LISTED', 'This names a source the draft does not hold.', path);
  };

  const seen = new Set<string>();
  for (const source of draft.sources) {
    const path = `source:${source.id}`;
    if (!UUID.test(source.id)) say('SOURCE_ID', 'A source is named by its file id.', path);
    if (seen.has(source.id)) say('SOURCE_TWICE', 'The same source is listed twice.', path);
    seen.add(source.id);
  }

  for (const imp of draft.imports) {
    if (!listed.has(imp.source)) sourceNamed(imp.source, `import:${imp.key}`);
  }

  for (const note of draft.notes) {
    const path = `note:${note.key}`;
    if (!within(note.text, STUDIO_LIMITS.noteText)) say('NOTE_TEXT', `A note says 1 to ${STUDIO_LIMITS.noteText} characters.`, path);
    if (note.about !== 'workspace' && !studioPartExists(draft, note.about)) {
      say('NOTE_ABOUT_UNKNOWN', `The note is about "${note.about}", which is not in the draft.`, path);
    }
    for (const id of note.sources) sourceNamed(id, path);
    if (note.card !== undefined && !draft.boardCards.some((card) => card.key === note.card)) {
      say('NOTE_CARD_UNKNOWN', `The note is answered by "${note.card}", which is not a Dashboard card of the draft.`, path);
    }
  }

  for (const item of draft.notYet) {
    const path = `not_yet:${item.key}`;
    if (
      !within(item.name, 80) ||
      !within(item.fit, 200) ||
      !within(item.archetype, 60) ||
      !within(item.need, 280) ||
      !(UNMET_NEED_LEVELS as readonly string[]).includes(item.level)
    ) {
      say('NOT_YET_TEXT', 'A not-yet item names itself in 1 to 80 characters, its fit in 1 to 200, the kind of business in 1 to 60, the need in 1 to 280, and a level.', path);
    }
  }

  for (const entry of draft.from) {
    if (!studioPartExists(draft, entry.path)) {
      say('FROM_PART_UNKNOWN', `Where "${entry.path}" came from is kept, and the part is not in the draft.`, entry.path);
      continue;
    }
    if (entry.from.length > STUDIO_LIMITS.fromPerPart) {
      say('FROM_TOO_MANY', `A part keeps ${STUDIO_LIMITS.fromPerPart} origins at most.`, entry.path);
    }
    for (const one of entry.from) {
      if ((one.kind === 'said' || one.kind === 'source') && one.quote !== undefined && one.quote.length > STUDIO_LIMITS.quote) {
        say('QUOTE_TOO_LONG', `A quote is ${STUDIO_LIMITS.quote} characters at most.`, entry.path);
      }
      if (one.kind === 'source') sourceNamed(one.source, entry.path);
    }
  }

  const gaps = new Set<string>();
  for (const { gap, reason } of draft.dismissedGaps) {
    const path = `gap:${gap}`;
    if (gap.length === 0 || gap.length > GAP_ID_MAX) say('GAP_ID', `A gap's id is 1 to ${GAP_ID_MAX} characters.`, path);
    if (gaps.has(gap)) say('GAP_TWICE', 'The same gap is left twice.', path);
    gaps.add(gap);
    if (reason !== undefined && reason.length > STUDIO_LIMITS.reason) say('GAP_REASON', `A reason is ${STUDIO_LIMITS.reason} characters at most.`, path);
  }
  return problems;
};
